import React, { useCallback, useEffect, useState } from 'react';
import { 
  Box, Paper, Typography, TextField, Button, 
  Alert, Divider, Stack, Switch, Chip, IconButton, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import KeyIcon from '@mui/icons-material/Key';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddchartIcon from '@mui/icons-material/Addchart';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import api from '../../services/apiClient';
import { useSnackbar } from '../../contexts/SnackbarContext';
import {
  ChannelReportRun,
  ChannelReportType,
  downloadChannelReportPdf,
  generateChannelReport,
  getChannelReportPreference,
  getChannelReports,
  updateChannelReportPreference,
} from '../../services/billingService';
import { useAuth } from '../../hooks';
import { updateProfile } from '../../services/usersService';

const cardSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  height: '100%',
} as const;

const labelSx = {
  fontSize: '0.68rem',
  fontWeight: 800,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
} as const;

const reportTypeLabel = (value: string) => ({
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  MONTH_TO_DATE: 'Month to date',
}[value] || value);

const formatReportDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Africa/Nairobi',
  });
};

export const PortalSecurity: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const { user, refetchUser } = useAuth();
  
  // Password State
  const [passData, setPassData] = useState({ current: '', new: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    city: '',
    postalCode: '',
    country: '',
    altPhone: '',
  });
  const [reportDownloading, setReportDownloading] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportType, setReportType] = useState<'ALL' | ChannelReportType>('ALL');
  const [generateType, setGenerateType] = useState<ChannelReportType>('DAILY');
  const [reports, setReports] = useState<ChannelReportRun[]>([]);

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(true);
  const [monthlyReports, setMonthlyReports] = useState(true);

  const isProfileComplete = !!(user?.firstName && user?.lastName && user?.country);

  useEffect(() => {
    if (!user) return;
    setFormState({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      city: user.city || '',
      postalCode: user.postalCode || '',
      country: user.country || '',
      altPhone: user.altPhone || '',
    });
    if (!isProfileComplete) setIsEditingProfile(true);
  }, [user, isProfileComplete]);

  const handlePassChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassData({ ...passData, [e.target.name]: e.target.value });
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProfileSave = async () => {
    if (!formState.firstName || !formState.lastName || !formState.country) {
      showSnackbar('First Name, Last Name, and Country are required.', 'error');
      return;
    }
    setProfileLoading(true);
    try {
      await updateProfile(formState);
      await refetchUser();
      setIsEditingProfile(false);
      showSnackbar('Profile updated successfully.', 'success');
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to update profile.', 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCancelProfileEdit = () => {
    if (!isProfileComplete) {
      showSnackbar('Profile must be completed.', 'warning');
      return;
    }
    setIsEditingProfile(false);
    if (!user) return;
    setFormState({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      city: user.city || '',
      postalCode: user.postalCode || '',
      country: user.country || '',
      altPhone: user.altPhone || '',
    });
  };

  const submitPassword = async () => {
    if (passData.new !== passData.confirm) {
      showSnackbar("New passwords do not match.", "error");
      return;
    }
    if (passData.new.length < 6) {
        showSnackbar("Password must be at least 6 characters.", "error");
        return;
    }
    
    setLoading(true);
    try {
      await api.put('/users/me/password', {
        currentPassword: passData.current,
        newPassword: passData.new
      });
      showSnackbar("Password updated successfully.", "success");
      setPassData({ current: '', new: '', confirm: '' });
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || "Failed to update password.", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadReports = useCallback(async () => {
    setReportLoading(true);
    try {
      const items = await getChannelReports(reportType === 'ALL' ? undefined : reportType);
      setReports(items);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to load channel reports.', 'error');
    } finally {
      setReportLoading(false);
    }
  }, [reportType]);

  useEffect(() => {
    getChannelReportPreference()
      .then((value) => setMonthlyReports(value.monthlyEmailEnabled !== false))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleMonthlyReportPreference = async (enabled: boolean) => {
    const previous = monthlyReports;
    setMonthlyReports(enabled);
    try {
      await updateChannelReportPreference(enabled);
      showSnackbar(`Monthly report email ${enabled ? 'enabled' : 'disabled'}.`, 'success');
    } catch (err: any) {
      setMonthlyReports(previous);
      showSnackbar(err.response?.data?.message || 'Failed to update report preference.', 'error');
    }
  };

  const handleGenerateReport = async () => {
    setReportGenerating(true);
    try {
      const generated = await generateChannelReport(generateType);
      showSnackbar(`${reportTypeLabel(generated.type)} report is ready.`, 'success');
      await loadReports();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to generate channel report.', 'error');
    } finally {
      setReportGenerating(false);
    }
  };

  const handleDownloadChannelReport = async (report: ChannelReportRun) => {
    setReportDownloading(report.id);
    try {
      const blob = await downloadChannelReportPdf(report.id);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = report.fileName || `streamy-channel-report-${report.id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Channel report downloaded.', 'success');
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to download channel report.', 'error');
    } finally {
      setReportDownloading(null);
    }
  };

  return (
    <Box maxWidth="lg">
      <Typography variant="h6" fontWeight={900} sx={{ mb: 0.5 }}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Password, account protection, billing delivery, and report preferences.</Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: -1.5 }}>
        <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
          <Paper variant="outlined" sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountCircleIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight={900}>Your Profile</Typography>
                  <Typography sx={labelSx}>Billing identity</Typography>
                </Box>
              </Box>
              {isProfileComplete && !isEditingProfile && (
                <Button size="small" variant="outlined" onClick={() => setIsEditingProfile(true)} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>
                  Edit
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            {!isProfileComplete && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 0 }}>
                Complete your profile to unlock full account access.
              </Alert>
            )}
            <fieldset disabled={!isEditingProfile} style={{ border: 'none', padding: 0, margin: 0, opacity: !isEditingProfile ? 0.82 : 1 }}>
              <Stack spacing={2}>
                <TextField label="Email Address" value={user?.email || ''} size="small" fullWidth disabled />
                <TextField label="Phone Number" value={user?.phone || ''} size="small" fullWidth disabled />
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField label="First Name" name="firstName" value={formState.firstName} onChange={handleProfileChange} size="small" fullWidth required />
                  <TextField label="Last Name" name="lastName" value={formState.lastName} onChange={handleProfileChange} size="small" fullWidth required />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField label="City" name="city" value={formState.city} onChange={handleProfileChange} size="small" fullWidth />
                  <TextField label="Postal Code" name="postalCode" value={formState.postalCode} onChange={handleProfileChange} size="small" fullWidth />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField label="Country" name="country" value={formState.country} onChange={handleProfileChange} size="small" fullWidth required />
                  <TextField label="Alt Phone" name="altPhone" value={formState.altPhone} onChange={handleProfileChange} size="small" fullWidth />
                </Box>
              </Stack>
            </fieldset>
            {isEditingProfile && (
              <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={handleCancelProfileEdit} disabled={profileLoading || !isProfileComplete} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleProfileSave} disabled={profileLoading} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>
                  {profileLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Stack>
            )}
          </Paper>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
          <Paper variant="outlined" sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <KeyIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight={900}>Change Password</Typography>
                  <Typography sx={labelSx}>Account credentials</Typography>
                </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Stack spacing={2}>
                <TextField 
                    label="Current Password" 
                    type="password"
                    name="current"
                    value={passData.current}
                    onChange={handlePassChange}
                    size="small" fullWidth 
                />
                <TextField 
                    label="New Password" 
                    type="password"
                    name="new"
                    value={passData.new}
                    onChange={handlePassChange}
                    size="small" fullWidth 
                />
                <TextField 
                    label="Confirm New Password" 
                    type="password"
                    name="confirm"
                    value={passData.confirm}
                    onChange={handlePassChange}
                    size="small" fullWidth 
                />
                
                <Box sx={{ pt: 1 }}>
                    <Button 
                        variant="contained" 
                        disabled={loading || !passData.current || !passData.new}
                        onClick={submitPassword}
                        sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}
                    >
                        {loading ? "Updating..." : "Update Password"}
                    </Button>
                </Box>
            </Stack>
          </Paper>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
          <Paper variant="outlined" sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShieldIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight={900}>Account Protection</Typography>
                  <Typography sx={labelSx}>Session controls</Typography>
                </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="body2" fontWeight={600}>Two-Factor Authentication</Typography>
                        <Typography variant="caption" color="text.secondary">Add an extra layer of security.</Typography>
                    </Box>
                    <Switch checked={mfaEnabled} onChange={(e) => setMfaEnabled(e.target.checked)} />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="body2" fontWeight={600}>Strict Session Timeout</Typography>
                        <Typography variant="caption" color="text.secondary">Auto-logout after 15 mins of inactivity.</Typography>
                    </Box>
                    <Switch checked={sessionTimeout} onChange={(e) => setSessionTimeout(e.target.checked)} />
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                    For Stream Key resets, please navigate to the <b>Resources</b> tab.
                </Alert>
                
                <Box sx={{ mt: 'auto', pt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" color="error" startIcon={<LockIcon />} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>
                        Sign Out All Devices
                    </Button>
                </Box>
            </Stack>
          </Paper>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
          <Paper variant="outlined" sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ReceiptLongIcon color="primary" />
              <Box>
                <Typography variant="subtitle1" fontWeight={900}>Billing Settings</Typography>
                <Typography sx={labelSx}>Invoice delivery</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                  <EmailIcon color="primary" fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight={800}>Receive invoice via Email</Typography>
                    <Typography variant="caption" color="text.secondary">Required for proforma and paid invoice delivery.</Typography>
                  </Box>
                </Box>
                <Switch checked disabled size="small" />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                  <WhatsAppIcon color="disabled" fontSize="small" />
                  <Box>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2" fontWeight={800}>Receive invoice via WhatsApp</Typography>
                      <Chip label="Coming soon" size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem', fontWeight: 800 }} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">Disabled until WhatsApp delivery is available.</Typography>
                  </Box>
                </Box>
                <Switch checked={false} disabled size="small" />
              </Box>
            </Stack>
          </Paper>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
          <Paper variant="outlined" sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AssessmentIcon color="primary" />
              <Box>
                <Typography variant="subtitle1" fontWeight={900}>Reports</Typography>
                <Typography sx={labelSx}>Channel reporting</Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" fontWeight={800}>Receive monthly channel reports</Typography>
                  <Typography variant="caption" color="text.secondary">Monthly reports are enabled by default.</Typography>
                </Box>
                <Switch checked={monthlyReports} onChange={(event) => handleMonthlyReportPreference(event.target.checked)} size="small" />
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" fontWeight={800}>Report archive</Typography>
                  <Typography variant="caption" color="text.secondary">Closed periods use 08:00 Africa/Nairobi boundaries.</Typography>
                </Box>
                <Tooltip title="Refresh report archive"><span><IconButton size="small" onClick={loadReports} disabled={reportLoading}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={reportType}
                onChange={(_, value) => value && setReportType(value)}
                sx={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}
              >
                {(['ALL', 'DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY'] as const).map((value) => (
                  <ToggleButton key={value} value={value} sx={{ px: 1.25, py: 0.45, fontSize: '0.66rem', fontWeight: 800 }}>
                    {value === 'ALL' ? 'All' : reportTypeLabel(value)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Generate previous"
                  value={generateType}
                  onChange={(event) => setGenerateType(event.target.value as ChannelReportType)}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: 170 }}
                >
                  <option value="DAILY">Day</option>
                  <option value="WEEKLY">Week</option>
                  <option value="MONTHLY">Month</option>
                  <option value="QUARTERLY">Quarter</option>
                  <option value="MONTH_TO_DATE">Month to date</option>
                </TextField>
                <Button variant="outlined" startIcon={<AddchartIcon />} onClick={handleGenerateReport} disabled={reportGenerating} sx={{ textTransform: 'none', fontWeight: 800 }}>
                  {reportGenerating ? 'Generating...' : 'Generate'}
                </Button>
              </Stack>
              <Stack spacing={0.75} sx={{ maxHeight: 230, overflowY: 'auto' }}>
                {reportLoading && <Typography variant="caption" color="text.secondary">Loading report archive...</Typography>}
                {!reportLoading && reports.length === 0 && <Alert severity="info">No generated reports match this period.</Alert>}
                {!reportLoading && reports.map((report) => (
                  <Box key={report.id} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1, alignItems: 'center', px: 1.25, py: 0.75, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="caption" fontWeight={900}>{reportTypeLabel(report.type)}</Typography>
                        <Chip label={report.status} size="small" color={report.status === 'READY' ? 'success' : report.status === 'FAILED' ? 'error' : 'default'} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800 }} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatReportDate(report.periodStart)} - {formatReportDate(report.periodEnd)} · Coverage {Number(report.summary?.dataCoveragePct || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                    <Tooltip title="Download PDF"><span><IconButton size="small" disabled={report.status !== 'READY' || reportDownloading === report.id} onClick={() => handleDownloadChannelReport(report)}><DownloadIcon fontSize="small" /></IconButton></span></Tooltip>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};
